import { useEffect, useState, type ReactNode } from 'react';
import { LAYOUT_LIMITS, type Chat, type Task } from '@jerome2/shared';
import { stopTurn } from '../chat/queue.ts';
import { money, relative, runningFor } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { app, saveSettings } from '../state/app.ts';
import { chatStore } from '../state/chat.ts';
import { showChat } from '../state/chatTabs.ts';
import { runningChatCount, runningChats, turnStart, turnStarts } from '../state/chatTurns.ts';
import { busyTaskCount, groupTasks, markSeen, taskActions, tasksStore } from '../state/tasks.ts';
import { openTab } from '../state/tabs.ts';
import { errorToast } from '../state/toast.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, Chip, Strip } from '../ui/primitives.tsx';

// Werkerskolom rechts: de taken in drie groepen (2.3.10, ticket 37198): Bezig (wachtend, bezig of met een vraag), Klaar, nog niet
// bekeken (klaar of mislukt en nog niet geopend, hoe oud ook) en Klaar en bekeken (van de laatste zeven dagen, met de chip Alles
// alles wat de client heeft), elk met voortgang, kosten en tijd; akkoordvragen bovenaan (de knoppen staan uit zolang het besluit
// onderweg is). Klik opent de taak bij Verwerkt in Mail en zet een klare taak op bekeken (`markSeen`). Op een breed scherm klapt de kolom in tot de strook `WorkersStrip` (44 px) en weer uit, en is hij zo
// breed als ingesteld (`width`, standaard 280 px; de greep op de rand staat in de schil, ticket 28376).
// Werker per vraag (ticket 30961, deel 2): boven de taken staat elk gewoon gesprek waar Jerome mee bezig is (ook dat van een tabblad
// van een mail) als rij met de titel, hoe lang het al loopt en Stop, tot het antwoord er is (state/chatTurns.ts); een klik op de rij
// toont het gesprek (`showChat`) en sluit het vel (`onOpened`). De kop, de strook en de bliksem op de telefoon tellen ze mee.

export function statusTone(status: Task['status']): 'blue' | 'amber' | 'green' | 'red' | 'grey' {
  if (status === 'done') return 'blue';
  if (status === 'question') return 'amber';
  if (status === 'failed') return 'red';
  if (status === 'finished') return 'green';
  return 'grey';
}

export function statusLabel(t: Task): string {
  switch (t.status) {
    case 'queued':
      return 'Wacht';
    case 'running':
      return t.progress ?? 'Bezig';
    case 'done':
      return t.result?.title ?? 'Klaar';
    case 'question':
      return t.question ? `Vraag: ${t.question}` : 'Vraag';
    case 'failed':
      return t.error ? `Mislukt: ${t.error}` : 'Mislukt';
    case 'finished':
      return 'Afgerond';
    case 'cancelled':
      return 'Geannuleerd';
  }
}

export function openTask(task: Task): void {
  void markSeen(task);
  openTab('mail', { single: true, state: { view: 'processed', processed: 'all', selectedId: task.messageId, taskId: task.id } });
}

/** De klok voor de tijd bij een gesprek: tikt elke seconde zolang `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [on]);
  return now;
}

/** `embedded`: in een vel op de telefoon (geen eigen rand of breedte; de titel staat op het vel). `onCollapse`: de knop in de kop die
 *  de kolom tot de strook (`WorkersStrip`) inklapt; in een vel is die er niet, want het vel sluit zelf. `width`: de breedte naast het
 *  werk in px. `onOpened`: na een klik op een gesprek (het vel sluit dan). */
export function WorkersRail(props: { embedded?: boolean | undefined; onCollapse?: (() => void) | undefined; width?: number | undefined; onOpened?: (() => void) | undefined }) {
  const showAll = useStore(app, (s) => s.settings.workersRail.showAll);
  const visible = useStore(app, (s) => s.settings.workersRail.visible);
  const state = useStore(tasksStore);
  const groups = groupTasks(state, { all: showAll });
  const chats = useStore(chatStore, (s) => s.chats);
  const starts = useStore(turnStarts);
  const turns = runningChats(chats, starts);
  const now = useNow(turns.length > 0);
  const busy = busyTaskCount(state) + turns.length;
  const ready = groups.unseen.length;
  const empty = !groups.busy.length && !groups.unseen.length && !groups.seen.length;
  const openChat = (chatId: string) => {
    showChat(chatId);
    props.onOpened?.();
  };
  // Op de telefoon staat de kolom in een vel; de instelling "zichtbaar" van de desktop blijft wat hij is.
  const setShowAll = (all: boolean) => void saveSettings({ workersRail: { visible: props.embedded ? visible : true, showAll: all } });
  // Een akkoordvraag waarvan het besluit onderweg is: één keer, en een fout als melding.
  const [deciding, setDeciding] = useState<string | null>(null);
  const decide = (id: string, allow: boolean) => {
    setDeciding(id);
    taskActions
      .decidePermission(id, allow)
      .catch(errorToast)
      .finally(() => setDeciding((d) => (d === id ? null : d)));
  };

  return (
    <aside className={`flex flex-col bg-surface ${props.embedded ? 'h-full' : 'shrink-0 border-l border-line'}`} style={props.embedded ? undefined : { width: props.width ?? LAYOUT_LIMITS.workersRail.default }} aria-label="Werkers">
      <div className="h-11 shrink-0 flex items-center gap-2 px-3 border-b border-line">
        {props.embedded ? null : <span className="font-medium">Werkers</span>}
        <span className="text-[13px] text-muted truncate min-w-0">
          <span>{busy ? `${busy} bezig` : 'rustig'}</span>
          {ready ? <span data-testid="werkers-klaar-teller">, {ready} klaar voor jou</span> : null}
        </span>
        <div className="ml-auto flex items-center gap-1">
          <Chip active={!showAll} onClick={() => setShowAll(false)} title="Bezig, klaar en nog niet bekeken, en wat je de laatste zeven dagen bekeek" className="h-7 px-2.5 text-[13px] whitespace-nowrap">
            7 dagen
          </Chip>
          <Chip active={showAll} onClick={() => setShowAll(true)} title="Ook wat je eerder bekeek" className="h-7 px-2.5 text-[13px]">
            Alles
          </Chip>
          {props.onCollapse ? (
            <button type="button" aria-label="Werkers inklappen" title="Werkers inklappen" onClick={props.onCollapse} className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md text-ink-soft hover:bg-tint hover:text-ink">
              <Icon name="chevron" size={16} />
            </button>
          ) : null}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        {state.permissions.length ? (
          <div className="p-2 border-b border-line bg-amber-soft/60">
            {state.permissions.map((p) => (
              <div key={p.id} className="rounded-md bg-surface border border-amber/30 p-2.5 mb-2 last:mb-0 text-[13px]">
                <div className="font-medium text-amber mb-1">Jerome wil iets doen dat jouw akkoord vraagt</div>
                <div className="text-ink-soft break-words">{p.tool.replace(/^mcp__\w+__/, '')} {p.inputSummary ? `(${p.inputSummary})` : ''}</div>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="primary" onClick={() => decide(p.id, true)} busy={deciding === p.id} disabled={deciding !== null}>
                    Akkoord
                  </Button>
                  <Button size="sm" onClick={() => decide(p.id, false)} disabled={deciding !== null}>
                    Niet
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {turns.length || groups.busy.length ? (
          <WorkerGroup testId="werkers-bezig" title="Bezig">
            {turns.length ? (
              <ul aria-label="Vragen in gesprekken">
                {turns.map((c) => (
                  <ChatTurnRow key={c.id} chat={c} since={turnStart(c, starts, now)} now={now} onOpen={() => openChat(c.id)} />
                ))}
              </ul>
            ) : null}
            <TaskList tasks={groups.busy} />
          </WorkerGroup>
        ) : null}
        {groups.unseen.length ? (
          <WorkerGroup testId="werkers-klaar-nieuw" title="Klaar, nog niet bekeken">
            <TaskList tasks={groups.unseen} />
          </WorkerGroup>
        ) : null}
        {groups.seen.length ? (
          <WorkerGroup testId="werkers-klaar-bekeken" title="Klaar en bekeken">
            <TaskList tasks={groups.seen} />
          </WorkerGroup>
        ) : null}
        {empty && !turns.length ? (
          <div className="p-6 text-center text-muted text-[13px]">
            <Icon name="bolt" size={22} className="mx-auto mb-2 opacity-60" />
            {showAll ? 'Nog geen taken.' : 'Geen taken bezig. Geef een mail een instructie en Jerome gaat aan het werk.'}
          </div>
        ) : null}
      </div>
    </aside>
  );
}

/** Een groep van de kolom met een zichtbare kop; een lege groep tekent de aanroeper niet. */
function WorkerGroup(props: { testId: string; title: string; children: ReactNode }) {
  return (
    <section data-testid={props.testId} aria-label={props.title}>
      <h3 className="px-3 pt-3 pb-1.5 text-[12px] font-medium uppercase tracking-wide text-muted border-b border-line">{props.title}</h3>
      {props.children}
    </section>
  );
}

function TaskList(props: { tasks: Task[] }) {
  if (!props.tasks.length) return null;
  return (
    <ul>
      {props.tasks.map((t) => (
        <li key={t.id} className="relative border-b border-line">
          <Strip tone={statusTone(t.status)} />
          <button type="button" className="w-full text-left pl-4 pr-3 py-2.5 hover:bg-tint/60" onClick={() => openTask(t)}>
            <div className="flex items-baseline gap-2">
              <span className="font-medium truncate flex-1">{t.subject || t.instruction}</span>
              <span className="text-[12px] text-muted shrink-0">{relative(t.updatedAt)}</span>
            </div>
            <div className="text-[13px] text-ink-soft truncate">{t.instruction}</div>
            <div className={`text-[13px] truncate flex items-center gap-1.5 ${t.status === 'question' ? 'text-amber' : t.status === 'failed' ? 'text-red' : t.status === 'done' ? 'text-accent' : 'text-muted'}`}>
              {t.status === 'running' ? <Icon name="spinner" size={12} className="animate-spin shrink-0" /> : null}
              <span className="truncate">{statusLabel(t)}</span>
              {t.costUsd > 0 ? <span className="ml-auto shrink-0 text-muted">{money(t.costUsd)}</span> : null}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Een gesprek waar Jerome mee bezig is (ticket 30961, deel 2): de titel over de volle breedte met hoe lang de beurt al loopt (`since`
 * tot `now`), en eronder Stop (`stopTurn`: wat nog wachtte, gaat terug naar het vak van het gesprek), zo hoog als de tweede regel.
 * Een klik op de rij toont het gesprek (`onOpen`); Stop ligt ernaast, niet erin, want een knop in een knop kan niet. */
function ChatTurnRow(props: { chat: Chat; since: number; now: number; onOpen: () => void }) {
  const { chat } = props;
  return (
    <li data-testid="werker-gesprek" className="relative border-b border-line">
      <Strip tone="grey" />
      <button type="button" className="w-full text-left pl-4 pr-3 py-2.5 hover:bg-tint/60" onClick={props.onOpen}>
        <div className="flex items-baseline gap-2">
          <span className="font-medium truncate flex-1">{chat.title}</span>
          <span data-testid="werker-tijd" className="text-[12px] text-muted shrink-0 tabular-nums">
            {runningFor(Math.max(props.now, props.since) - props.since)}
          </span>
        </div>
        <div className="min-h-7 pointer-coarse:min-h-11 pr-24 text-[13px] text-muted flex items-center gap-1.5">
          <Icon name="spinner" size={12} className="animate-spin shrink-0" />
          <span className="truncate">Bezig met je vraag</span>
        </div>
      </button>
      <Button size="sm" icon="stop" aria-label={`Stop ${chat.title}`} title="Stop" className="absolute right-3 bottom-2.5" onClick={() => stopTurn(chat.id)}>
        Stop
      </Button>
    </li>
  );
}

/** De ingeklapte kolom op een breed scherm: een strook van 44 px met de uitklapknop, het aantal bezig (taken en gesprekken) en een
 * oranje teken bij akkoordvragen. */
export function WorkersStrip(props: { onExpand: () => void }) {
  const state = useStore(tasksStore);
  const chatsBusy = useStore(chatStore, (s) => runningChatCount(s.chats));
  const busy = busyTaskCount(state) + chatsBusy;
  const questions = state.permissions.length;
  const summary = [busy ? `${busy} bezig` : 'rustig', questions ? `${questions} ${questions === 1 ? 'akkoordvraag' : 'akkoordvragen'}` : null].filter(Boolean).join(', ');
  return (
    <aside className="w-11 shrink-0 flex flex-col bg-surface border-l border-line" aria-label="Werkers">
      <button type="button" aria-label="Werkers uitklappen" title={`Werkers uitklappen (${summary})`} onClick={props.onExpand} className="flex-1 flex flex-col items-center gap-2 pt-1.5 pb-3 text-ink-soft hover:bg-tint hover:text-ink">
        <span className="h-8 w-8 inline-flex items-center justify-center rounded-md">
          <Icon name="chevron" size={16} className="rotate-180" />
        </span>
        {questions ? <span data-count="questions" className="h-5 min-w-5 px-1 rounded-full bg-amber text-white text-[12px] font-medium inline-flex items-center justify-center">{questions}</span> : null}
        {busy ? <span data-count="busy" className="h-5 min-w-5 px-1 rounded-full bg-accent text-white text-[12px] font-medium inline-flex items-center justify-center">{busy}</span> : null}
        <span className="text-[12px] font-medium [writing-mode:vertical-rl]">Werkers</span>
      </button>
    </aside>
  );
}
